/**
 * appealService.ts
 *
 * Step 6 — Appeal flow. Implements CLAUDE.md §G.3:
 *   - Filing window: 2 days after manager sign-off (i.e. status='manager_signed' or 'closed')
 *   - HR facilitates the meeting and issues the final decision
 *   - Closed appeals do not block Step 7; pending appeals do
 *
 * Auto-advance logic lives in stepAdvancer.ts and is called by a scheduled
 * Cloud Function (deploy out-of-scope for this repo; see scheduled function
 * stub at the bottom of stepAdvancer).
 */

import {
  doc, getDocs, query, where, collection,
  serverTimestamp, runTransaction, Timestamp, writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  AppealRecord, AppealStatus, AppealDecision, AppealAttendee,
} from '../types/appeal';
import { APPEAL_FILING_WINDOW_DAYS } from '../types/appeal';
import type { AppraisalForm } from '../types/appraisal';
import { getAppraisal } from './appraisalService';

const COL = 'appealRecords';

// ─── Errors ────────────────────────────────────────────────────────

export class AppealError extends Error {
  constructor(
    public code: 'not_found' | 'wrong_state' | 'window_closed' | 'unauthorized',
    message: string,
  ) {
    super(message);
    this.name = 'AppealError';
  }
}

// ─── Filing ────────────────────────────────────────────────────────

export interface FileAppealInput {
  cycleId: string;
  employeeId: string;
  appraisalId: string;
  filedReason: string;
}

/**
 * File an appeal. Enforces the 2-day window per §G.3 by reading the
 * manager-signed timestamp from the appraisal. Refuses to file if no
 * manager signature exists (Step 5 not complete) or if the window has
 * closed.
 */
export async function fileAppeal(input: FileAppealInput): Promise<AppealRecord> {
  if (!input.filedReason.trim()) {
    throw new AppealError('wrong_state', 'Alasan appeal wajib diisi.');
  }
  const appraisal = await getAppraisal(input.appraisalId);
  if (!appraisal) throw new AppealError('not_found', 'Appraisal not found.');
  if (appraisal.employeeId !== input.employeeId) {
    throw new AppealError('unauthorized', 'Not your appraisal.');
  }
  if (!appraisal.managerSignature) {
    throw new AppealError(
      'wrong_state',
      'Appeal can only be filed after manager has signed (Step 5 complete).',
    );
  }

  const now = Date.now();
  const signedAt = appraisal.managerSignature.signedAt.toDate().getTime();
  const windowMs = APPEAL_FILING_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const filedWithinWindow = (now - signedAt) <= windowMs;
  if (!filedWithinWindow) {
    throw new AppealError(
      'window_closed',
      `Appeal window closed ${Math.round(((now - signedAt) - windowMs) / 86400000)} day(s) ago.`,
    );
  }

  const data: Omit<AppealRecord, 'id'> = {
    cycleId:     input.cycleId,
    employeeId:  input.employeeId,
    appraisalId: input.appraisalId,
    filedAt:     serverTimestamp() as unknown as Timestamp,
    filedWithinWindow,
    filedReason: input.filedReason.trim(),
    status:      'open' as AppealStatus,
    createdAt:   serverTimestamp() as unknown as Timestamp,
  };

  // Atomic two-write commit: appeal create + appraisal back-reference.
  // Without this batch, a partial failure leaves either an orphan appeal
  // or a back-ref pointing nowhere.
  const appealRef = doc(collection(db, COL));
  const batch = writeBatch(db);
  batch.set(appealRef, data);
  batch.set(
    doc(db, 'appraisalForms', input.appraisalId),
    { appealId: appealRef.id },
    { merge: true },
  );
  await batch.commit();

  return { ...data, id: appealRef.id, filedAt: Timestamp.fromDate(new Date(now)) };
}

// ─── HR processing ─────────────────────────────────────────────────

export async function scheduleAppealMeeting(
  appealId: string,
  meetingAt: Date,
  attendees: AppealAttendee[],
): Promise<void> {
  await runTransaction(db, async tx => {
    const ref = doc(db, COL, appealId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new AppealError('not_found', 'Appeal not found.');
    const a = snap.data() as AppealRecord;
    if (a.status === 'decided') {
      throw new AppealError('wrong_state', 'Appeal already decided.');
    }
    tx.update(ref, {
      meetingScheduledAt: Timestamp.fromDate(meetingAt),
      attendees,
      status: 'in_meeting' as AppealStatus,
      updatedAt: serverTimestamp(),
    });
  });
}

export interface DecideAppealInput {
  appealId: string;
  decision: AppealDecision;
  decisionBy: string;
  decisionNotes?: string;
}

export async function decideAppeal(input: DecideAppealInput): Promise<AppealRecord> {
  return runTransaction(db, async tx => {
    const ref = doc(db, COL, input.appealId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new AppealError('not_found', 'Appeal not found.');
    const a = snap.data() as AppealRecord;
    if (a.status === 'decided') return { ...a, id: input.appealId };

    tx.update(ref, {
      decision:      input.decision,
      decisionBy:    input.decisionBy,
      decidedAt:     serverTimestamp(),
      decisionNotes: input.decisionNotes ?? null,
      status:        'decided' as AppealStatus,
      updatedAt:     serverTimestamp(),
    });
    return {
      ...a,
      id: input.appealId,
      decision: input.decision,
      decisionBy: input.decisionBy,
      decidedAt: Timestamp.fromDate(new Date()),
      decisionNotes: input.decisionNotes,
      status: 'decided',
    };
  });
}

// ─── Read ──────────────────────────────────────────────────────────

export async function getAppealByAppraisal(appraisalId: string): Promise<AppealRecord | null> {
  const q = query(collection(db, COL), where('appraisalId', '==', appraisalId));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...(d.data() as Omit<AppealRecord, 'id'>) };
}

export async function listOpenAppealsForCycle(cycleId: string): Promise<AppealRecord[]> {
  const q = query(
    collection(db, COL),
    where('cycleId', '==', cycleId),
    where('status', 'in', ['open', 'in_meeting']),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<AppealRecord, 'id'>) }));
}

// ─── Window helper ─────────────────────────────────────────────────

/**
 * Is the appeal window still open for this appraisal? Caller may use this
 * to gate the "File Appeal" button in the employee view.
 */
export function isAppealWindowOpen(appraisal: AppraisalForm): boolean {
  if (!appraisal.managerSignature) return false;
  const now = Date.now();
  const signedAt = appraisal.managerSignature.signedAt.toDate().getTime();
  const windowMs = APPEAL_FILING_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  return (now - signedAt) <= windowMs;
}
